import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { XMarkIcon, ArrowPathIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useMonitorValues } from '../../../hooks/useMonitors';
import monitorsService from '../../../services/monitorsService';

const DAY_OPTIONS = [
  { label: '7 days', value: 7, limit: 200 },
  { label: '30 days', value: 30, limit: 500 },
  { label: '90 days', value: 90, limit: 1000 },
];

// Full-size history modal for a monitor. Replaces the old
// MetricHistoryModal but reads monitor_values via the React Query hook,
// and overlays threshold lines from the monitor's evaluation_config
// (only meaningful for evaluation_mode='threshold' — statistical
// monitors don't have fixed lines to draw).
const MonitorHistoryModal = ({ isOpen, onClose, monitor, connectionId }) => {
  const [windowIdx, setWindowIdx] = useState(1); // default 30d
  const dayOpt = DAY_OPTIONS[windowIdx];

  const { data: values = [], isLoading, error } = useMonitorValues(
    connectionId,
    monitor?.id,
    { limit: dayOpt.limit, enabled: isOpen && !!monitor?.id }
  );

  if (!isOpen || !monitor) return null;

  // Same detection as MonitorCard — recognize segmentation from
  // segment_by OR (custom_sql + source_config.segment_column) so older
  // custom_sql monitors don't slip past as "scalar".
  const isSegmented =
    !!monitor.segment_by ||
    (monitor.source_type === 'custom_sql' &&
      !!monitor.source_config?.segment_column);

  // Prefer engine-computed aggregate rows (segment_value=NULL) for the
  // history chart — these are the un-grouped overall values, not an
  // approximation. Fall back to client-side aggregation when the
  // engine hasn't populated aggregates (old built-in monitor not yet
  // re-run, or custom_sql which doesn't get auto-aggregates). The
  // client-side aggregation method is the user's choice on the source
  // config (sum / avg / min / max), defaulting to 'sum' for legacy
  // monitors that predate the field.
  const aggregationMethod =
    monitor.source_config?.aggregation_method || 'sum';
  let sourceValues;
  if (!isSegmented) {
    sourceValues = values;
  } else {
    const nullSegmentRows = values.filter(
      (v) => v.segment_value === null || v.segment_value === undefined,
    );
    sourceValues = nullSegmentRows.length > 0
      ? nullSegmentRows
      : monitorsService.aggregateValuesByTimestamp(values, aggregationMethod);
  }

  // Backend returns DESC; flip for left-to-right plotting.
  const chartData = [...sourceValues]
    .reverse()
    .map((row) => ({
      ts: row.calculated_at,
      tsLabel: new Date(row.calculated_at).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      }),
      value:
        typeof row.value === 'number' ? row.value : Number(row.value),
    }))
    .filter((p) => Number.isFinite(p.value));

  // Threshold lines: only show for evaluation_mode='threshold' AND scalar
  // monitors. For segmented monitors the threshold applies per-segment, so
  // overlaying it on the avg-aggregate trend would be misleading (the
  // aggregate could be safely above the threshold while several segments
  // sit below it). Per-segment threshold overlays are a future iteration.
  const evalCfg = monitor.evaluation_config || {};
  const isThreshold = monitor.evaluation_mode === 'threshold';
  const showThresholdLines = isThreshold && !isSegmented;
  const warning = showThresholdLines ? evalCfg.warning : null;
  const critical = showThresholdLines ? evalCfg.critical : null;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const point = payload[0].payload;
    return (
      <div className="bg-white p-2 border border-gray-200 rounded shadow-sm">
        <div className="text-xs text-gray-500">
          {new Date(point.ts).toLocaleString()}
        </div>
        <div className="text-sm font-medium text-gray-900">
          {Number(point.value).toLocaleString()}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {monitor.display_name || monitor.name}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Historical values, last {dayOpt.value} day
                {dayOpt.value === 1 ? '' : 's'}.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Window controls */}
          <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Window:</span>
              {DAY_OPTIONS.map((opt, idx) => (
                <button
                  key={opt.value}
                  onClick={() => setWindowIdx(idx)}
                  className={`px-3 py-1 text-sm rounded-md ${
                    windowIdx === idx
                      ? 'bg-indigo-100 text-indigo-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="text-xs text-gray-500 flex items-center">
              <ClockIcon className="h-3 w-3 mr-1" />
              {values.length} data point{values.length === 1 ? '' : 's'}
            </div>
          </div>

          {/* Chart */}
          <div className="p-6">
            {isLoading ? (
              <div className="h-72 flex items-center justify-center text-gray-500">
                <ArrowPathIcon className="h-5 w-5 animate-spin mr-2" />
                Loading history...
              </div>
            ) : error ? (
              <div className="h-72 flex items-center justify-center text-sm text-red-600">
                {error.message || 'Failed to load history'}
              </div>
            ) : chartData.length < 2 ? (
              <div className="h-72 flex flex-col items-center justify-center text-sm text-gray-500">
                <p>Not enough history to plot a trend.</p>
                <p className="mt-1 text-xs text-gray-400">
                  Run this monitor a few more times to see how it changes over time.
                </p>
              </div>
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 10, right: 30, bottom: 10, left: 10 }}
                  >
                    <CartesianGrid stroke="#f3f4f6" />
                    <XAxis
                      dataKey="tsLabel"
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                      tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {warning !== null && warning !== undefined && (
                      <ReferenceLine
                        y={warning}
                        stroke="#f59e0b"
                        strokeDasharray="4 4"
                        label={{
                          value: 'Warning',
                          position: 'right',
                          fill: '#f59e0b',
                          fontSize: 11,
                        }}
                      />
                    )}
                    {critical !== null && critical !== undefined && (
                      <ReferenceLine
                        y={critical}
                        stroke="#ef4444"
                        strokeDasharray="4 4"
                        label={{
                          value: 'Critical',
                          position: 'right',
                          fill: '#ef4444',
                          fontSize: 11,
                        }}
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#6366f1"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonitorHistoryModal;
